import {
  ITEMS_RECEIVED_LABEL,
  SPEC_SCALE_LABEL,
  type ItemReceivedKey,
  type ItemsReceived,
  type KitSpecification,
} from '../kit/kit';
import { SPAN_KIND_LABEL, type SpanKind } from '../teeth/spans';

/**
 * What Accounts are shown of a case edited after intake, and the review of it.
 * The request of 5 October 2026 (third), section 3.
 *
 * A case edited after it was priced waits on Accounts' Changed list until
 * somebody squares the bill with the edit and marks it reviewed. Until this
 * request the case carried one sentence saying what changed, with the new value
 * at most. The accountant asked to see what each field read before and what it
 * reads now, who made the edit and when, and to be able to take a review back.
 *
 * So every edit is kept as a change of its own, with the fields it changed, and
 * a review covers every change that waits on the case and can be taken back as
 * the round it was.
 *
 * The server enforces all of it. This file is the same rule, worded the same
 * way, as the API's AccountsReview.cs: the keys, their order, the words a value
 * is shown in and the refusals, so the mock server answers as the API does and
 * the screen's labels are keyed by one list.
 */

/**
 * The keys a changed field goes by, in the one order a change lists them in.
 * The server's ChangeFields.All is the same list in the same order. `span` is a
 * bridge or bar added or taken off, one entry each, so that key may repeat;
 * `value` is what the case is worth, when the edit moved it.
 */
export const CHANGE_FIELDS = [
  'doctor',
  'patient',
  'origin',
  'input',
  'item',
  'shade',
  'instructions',
  'translucency',
  'anatomy',
  'incisal',
  'occlusal',
  'ingot',
  'block',
  'stain',
  'itemsReceived',
  'teeth',
  'units',
  'span',
  'value',
] as const;

export type ChangeField = (typeof CHANGE_FIELDS)[number];

export function isChangeField(value: string): value is ChangeField {
  return (CHANGE_FIELDS as readonly string[]).includes(value);
}

/** The two fields that hold a name: a doctor's or a patient's. */
export function isIdentityField(field: string): boolean {
  return field === 'doctor' || field === 'patient';
}

/**
 * The fields that change what a case costs: which item, which teeth and what
 * is made for each, how many, and what the case is worth. A case goes to
 * Accounts for review only when one of these changed. The request of
 * 7 October 2026, section 5: an edit to the patient, the instructions, the
 * shade, the dates or anything else of the kind is no business of the bill.
 */
export const BILLING_FIELDS = ['item', 'teeth', 'units', 'value'] as const;

export function isBillingField(field: string): boolean {
  return (BILLING_FIELDS as readonly string[]).includes(field);
}

/** The fields of a change that are the bill's, in the order they came. */
export function billingFields<T extends { field: string }>(fields: readonly T[]): T[] {
  return fields.filter((entry) => isBillingField(entry.field));
}

/**
 * What a change shows Accounts: the fields that are the bill's. A change kept
 * before the rule, which lists fields and none of them the bill's, shows what
 * it has, so that until it is cleared the reader can see why it is there.
 */
export function fieldsForAccounts<T extends { field: string }>(fields: readonly T[]): T[] {
  const billing = billingFields(fields);
  return billing.length > 0 ? billing : [...fields];
}

/**
 * Whether the changes waiting on a case need no review under the rule: each of
 * them kept its fields, and none of those is the bill's. A change from before
 * fields were kept is only a sentence; nobody can tell from it what changed, so
 * it still waits.
 */
export function needsNoReview(waiting: readonly { fields: readonly { field: string }[] }[]): boolean {
  return (
    waiting.length > 0 &&
    waiting.every((change) => change.fields.length > 0 && billingFields(change.fields).length === 0)
  );
}

/** The sentences of an edit that are the bill's, as the log writes them. */
const BILLING_SENTENCE = /^(changed the item to |changed the teeth to |cleared the teeth$|changed the units to )/;

/** Whether a sentence of the activity log is about the bill: the item, the teeth or the count. */
export function isBillingSentence(action: string): boolean {
  return BILLING_SENTENCE.test(action);
}

/** Where a field stands in the order. A key nobody knows goes last. */
export function changeFieldRank(field: string): number {
  const index = (CHANGE_FIELDS as readonly string[]).indexOf(field);
  return index < 0 ? CHANGE_FIELDS.length : index;
}

/**
 * One field of a case as an edit changed it, as the change keeps it: what it
 * read before and what it reads now, in the words the screen prints. Null is
 * nothing there, so a field filled in for the first time has no before and one
 * emptied has no after. `identity` marks a doctor's or patient's name, which is
 * kept whoever made the edit: who may read it is decided when the change is read.
 */
export interface KeptField {
  field: string;
  before: string | null;
  after: string | null;
  identity: boolean;
}

/**
 * The same field as one reader receives it. `hidden` is a name this reader may
 * not see: the entry stays, so they are told the name changed, and both values
 * are null.
 */
export interface ChangedField {
  field: string;
  before: string | null;
  after: string | null;
  hidden: boolean;
}

/**
 * One edit of a case: who made it and when, the sentence the Changed list
 * prints for it, and every field it changed. `id` is null and `fields` is empty
 * on a change that was waiting from before old values were recorded.
 */
export interface ChangeSet {
  id: string | null;
  at: string;
  byName: string | null;
  summary: string;
  fields: ChangedField[];
}

/** The review that stands on a case: when Accounts marked its changes reviewed, and who did. */
export interface AccountsReviewed {
  at: string;
  byName: string;
}

/**
 * `GET /job-orders/{jo}/accounts-review`: what there is to review on a case and
 * the review that stands on it. `since` is what Mark as reviewed sends back as
 * `seen`; null when nothing waits. `waiting` and `reviewed.sets` are oldest first.
 */
export interface AccountsReviewDetail {
  jobOrderNo: string;
  since: string | null;
  waiting: ChangeSet[];
  reviewed: (AccountsReviewed & { sets: ChangeSet[] }) | null;
}

/**
 * The refusals, worded once. The server says them, the mock says them the same
 * way, and the screen renders whichever arrives verbatim.
 */
export const ACCOUNTS_REVIEW_REFUSAL = {
  /** Marked reviewed with nothing waiting: somebody else was quicker. */
  nothingToReview: 'This case has no change waiting for Accounts.',
  /** The case was edited between Accounts reading its change and marking it reviewed. */
  changedAgain: 'This case was changed again. Read the new change before marking it reviewed.',
} as const;

/** What the activity log says of a review, and of one taken back. */
export const ACCOUNTS_REVIEW_ACTION = {
  reviewed: 'reviewed the change for billing',
  notReviewed: 'marked the change not reviewed',
  /** A change cleared because it was none of the bill's. The request of 7 October 2026, section 5. */
  cleared: 'cleared a change that needs no review by Accounts',
} as const;

/** The sentence for a change that moved the amount and none of the things that name one. */
export const ACCOUNTS_AMOUNT_CHANGED = 'changed the amount';

/** Who a change is stamped by when it was cleared as no business of the bill. */
export const ACCOUNTS_CLEARED_BY = 'Not a billing change';

/** The refusal of the clearing, for whoever is not management. */
export const ACCOUNTS_CLEANUP_REFUSAL = 'Only management may clear changes that need no review.';

/** The cases waiting for Accounts that need no review under the rule, as the server lists them. */
export interface ReviewCleanup {
  /** Every case waiting for Accounts. */
  pending: number;
  /** How many of them changed nothing of the bill. */
  needNoReview: number;
  items: { jobOrderNo: string; since: string | null; summary: string }[];
}

/** How long the order's waiting sentence may be; the oldest changes give way when it is full. */
export const ACCOUNTS_CHANGES_MAX = 1000;

/** How an empty list reads: no teeth on the chart, nothing received. */
export const CHANGE_NONE = 'None';

export type ChangeKind = 'added' | 'removed' | 'changed';

/**
 * How a field changed: filled in for the first time, emptied, or changed from
 * one value to another. A hidden name has neither value and reads as changed,
 * which is all its reader is told.
 */
export function changeKind(field: { before: string | null; after: string | null }): ChangeKind {
  if (field.before === null && field.after !== null) return 'added';
  if (field.before !== null && field.after === null) return 'removed';
  return 'changed';
}

/* ---- the waiting sentence ---- */

/** Whether there is anything written there: not null, not empty, not only spaces. */
function hasText(value: string | null | undefined): value is string {
  return value !== null && value !== undefined && value.trim() !== '';
}

/**
 * What changed, added to what was already waiting, oldest first. Kept within
 * the server's column by dropping the oldest changes, which the activity log
 * still has.
 */
export function appendChanges(waiting: string | null | undefined, change: string): string {
  const all = hasText(waiting) ? `${waiting}; ${change}` : change;
  if (all.length <= ACCOUNTS_CHANGES_MAX) return all;

  const tail = all.slice(-(ACCOUNTS_CHANGES_MAX - 2));
  const cut = tail.indexOf('; ');
  return `…${cut < 0 ? tail : tail.slice(cut + 2)}`;
}

/**
 * The waiting sentence put together again from the changes that wait, oldest
 * first, when a review is taken back. Null when none waits.
 */
export function rebuildChanges(summaries: readonly (string | null | undefined)[]): string | null {
  let all: string | null = null;
  for (const summary of summaries) {
    if (hasText(summary)) all = appendChanges(all, summary);
  }
  return all;
}

/**
 * Whether the change the screen showed is no longer the latest. The screen
 * sends back the `since` it read; an edit made after that has moved it on. A
 * request that sends nothing is from a build that never read one, and is let
 * through as it always was.
 */
export function changedSince(seen: string | null | undefined, since: string): boolean {
  return hasText(seen) && seen.trim() !== since;
}

/* ---- the words of a change ---- */

/** A value as a change shows it: null when there is nothing there to show. */
export function changeText(value: string | null | undefined): string | null {
  return hasText(value) ? value : null;
}

const ITEMS_RECEIVED_ORDER: readonly ItemReceivedKey[] = [
  'impression',
  'model',
  'bite',
  'implant',
  'abutment',
];

/**
 * What arrived with the case, in the order of the tick boxes and under their
 * labels: "Impression, Model". `CHANGE_NONE` when nothing did.
 */
export function itemsText(items: ItemsReceived): string {
  const names = ITEMS_RECEIVED_ORDER.filter((key) => items[key]).map(
    (key) => ITEMS_RECEIVED_LABEL[key],
  );
  return names.length === 0 ? CHANGE_NONE : names.join(', ');
}

/** A tooth with the item it carries: its own, or the case's headline item. */
export interface ToothWithItem {
  tooth: string;
  item: string;
}

/**
 * A chart on one line. Plain, the numbers in the order reception entered them:
 * "11, 12". Named, each item with its teeth, items in the order they first
 * appear: "Zircon Crown: 11, 12; E.max Veneer: 21". `CHANGE_NONE` for an empty
 * chart.
 */
export function teethText(teeth: readonly ToothWithItem[], named = false): string {
  if (teeth.length === 0) return CHANGE_NONE;
  if (!named) return teeth.map((entry) => entry.tooth).join(', ');

  const byItem = new Map<string, string[]>();
  for (const { tooth, item } of teeth) {
    const group = byItem.get(item);
    if (group === undefined) byItem.set(item, [tooth]);
    else group.push(tooth);
  }
  return [...byItem].map(([item, group]) => `${item}: ${group.join(', ')}`).join('; ');
}

/**
 * The teeth line of a change. While every tooth, before and after, carries the
 * same one item the numbers say it all. Once more than one item is in play the
 * items are named on both sides, so a change of item under the same teeth reads
 * as one. Teeth put in another order with nothing else changed read the same
 * named, so that one case falls back to the numbers.
 */
export function teethChange(
  before: readonly ToothWithItem[],
  after: readonly ToothWithItem[],
): KeptField {
  const named = new Set([...before, ...after].map((entry) => entry.item)).size > 1;
  const was = teethText(before, named);
  const now = teethText(after, named);

  return was === now
    ? { field: 'teeth', before: teethText(before), after: teethText(after), identity: false }
    : { field: 'teeth', before: was, after: now, identity: false };
}

/** A bridge or bar as a change names it: "Bridge B1 (11 to 13)", "Titanium bar T1 (33 to 43)". */
export function spanText(span: { kind: SpanKind; name: string; from: string; to: string }): string {
  return `${SPAN_KIND_LABEL[span.kind]} ${span.name} (${span.from} to ${span.to})`;
}

/**
 * A sum of money as a change carries it: the plain number, no currency and no
 * trailing zeros ("1200", "87.5"). The screen formats it as it formats every
 * amount.
 */
export function valueText(value: number): string {
  return String(Math.round(value * 100) / 100);
}

/** A step of the four scales as its label. A step nobody knows reads as itself. */
export function specScaleText(step: string): string {
  return Object.hasOwn(SPEC_SCALE_LABEL, step)
    ? SPEC_SCALE_LABEL[step as keyof typeof SPEC_SCALE_LABEL]
    : step;
}

/**
 * The specification, field by field: one entry for each of the seven that
 * differs, in the order of `CHANGE_FIELDS`. A step of the four scales reads as
 * its label; ingot, block and stain read as written, and as nothing when blank.
 */
export function specificationFields(before: KitSpecification, after: KitSpecification): KeptField[] {
  const fields: KeptField[] = [];

  for (const field of ['translucency', 'anatomy', 'incisal', 'occlusal'] as const) {
    if (before[field] !== after[field]) {
      fields.push({
        field,
        before: specScaleText(before[field]),
        after: specScaleText(after[field]),
        identity: false,
      });
    }
  }
  for (const field of ['ingot', 'block', 'stain'] as const) {
    if (before[field] !== after[field]) {
      fields.push({
        field,
        before: changeText(before[field]),
        after: changeText(after[field]),
        identity: false,
      });
    }
  }

  return fields;
}

/* ---- a change's fields ---- */

/**
 * The fields of one change in the one order every change lists them in,
 * whatever order the edit happened to compare them in. Entries under the same
 * key, the bridges and bars, keep the order they were given in.
 */
export function inChangeOrder<T extends { field: string }>(fields: readonly T[]): T[] {
  return [...fields].sort((a, b) => changeFieldRank(a.field) - changeFieldRank(b.field));
}

/**
 * The fields as this reader may have them. A doctor's or patient's name is
 * identity: without seeIdentity the entry stays, marked hidden, so the reader
 * knows the name changed, and both values go. Judged by the key as well as the
 * mark, so a change that lost its mark still hides the name.
 */
export function shownFields(fields: readonly KeptField[], seesIdentity: boolean): ChangedField[] {
  return fields.map((entry) => {
    const hidden = !seesIdentity && (entry.identity || isIdentityField(entry.field));
    return {
      field: entry.field,
      before: hidden ? null : entry.before,
      after: hidden ? null : entry.after,
      hidden,
    };
  });
}

/* ---- the rounds of a review ---- */

/** What a kept change needs for a review to find it: when it was made, and when it was reviewed. */
export interface ReviewableChange {
  id: string;
  at: string;
  reviewedAt: string | null;
}

/** Character by character, as the server compares an id: no locale has a say. */
function ordinal(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function oldestFirst(a: ReviewableChange, b: ReviewableChange): number {
  return ordinal(a.at, b.at) || ordinal(a.id, b.id);
}

/** The changes of a case still waiting for Accounts, oldest first. */
export function waitingChanges<T extends ReviewableChange>(changes: readonly T[]): T[] {
  return changes.filter((change) => change.reviewedAt === null).sort(oldestFirst);
}

/**
 * The changes one review covered, oldest first. A review stamps every change
 * that waited with the same instant and the order keeps that instant, so the
 * round is the changes that carry it. An earlier review's changes carry another
 * instant and are left where they are.
 */
export function reviewRound<T extends ReviewableChange>(
  changes: readonly T[],
  reviewedAt: string | null | undefined,
): T[] {
  if (reviewedAt === null || reviewedAt === undefined) return [];
  return changes.filter((change) => change.reviewedAt === reviewedAt).sort(oldestFirst);
}
